// render-my-worlds.ts — the world card renderers: "catalog-my-worlds", the creator's own worlds
// dashboard (my_worlds.php), and "catalog-worlds", the public Worlds browse (worlds.php). The
// endpoints ship pure data; this module owns the card shells and (for My Worlds) the management
// actions.

import type { CatalogListConfig, MyWorldCard, WorldBrowseCard } from './types';
import { initTooltips, resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';
import { buildEmptyState, emptyStateFromAttributes } from '../shared/empty-state';
import { editLocationUrl, listText, locationPictureUrl, locationUrl, type LocationListRow, type WorldListResponse } from './location-list-data';

type ManagedWorldCard = MyWorldCard & { csrf_token: string };

function worldCardFields(row: LocationListRow): Pick<MyWorldCard, 'item_id' | 'name' | 'url' | 'picture_url' | 'text'> {
    const name = listText(row.location_name);
    return { item_id: row.location_id, name, url: locationUrl(row.location_id),
        picture_url: locationPictureUrl(row) || `${BASE_URL_CDN}images/gm_tools/worlds.jpg`, text: name.toLowerCase() };
}

function myWorldCards(data: WorldListResponse): ManagedWorldCard[] {
    return data.worlds.map(row => ({ ...worldCardFields(row), edit_url: editLocationUrl(row.location_id),
        add_location_url: resolveUrl(`tools/locations/modify_location.php?location_master_parent_id=${encodeURIComponent(row.location_id)}`),
        media_url: resolveUrl(`tools/media/media_manager.php?type=location&id=${encodeURIComponent(row.location_id)}`),
        show_private: data.viewer_is_admin === true || Number(row.location_place_count) >= 50,
        is_private: Number(row.location_private) !== 0, csrf_token: data.csrf_token || '',
    }));
}

function worldBrowseCards(data: WorldListResponse): WorldBrowseCard[] {
    return data.worlds.map(row => ({ ...worldCardFields(row),
        timeline_url: resolveUrl(`world_timeline.php?world_location_id=${encodeURIComponent(row.location_id)}`),
        place_count: Number(row.location_active_place_count) || 0,
    })).sort((a, b) => b.place_count - a.place_count || (a.text < b.text ? -1 : a.text > b.text ? 1 : 0));
}

const CLAMP_2 = 'display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;';

function icon(id: string, w = 20, h = 20, title: string | boolean = false): string {
    return typeof iconMap === 'function' ? iconMap(id, w, h, title) : '';
}

function appendIconButton(
    parent: HTMLElement,
    href: string,
    id: string,
    className: string,
    iconId: string,
    ariaLabel: string,
    title: string | boolean = false,
): void {
    const a = document.createElement('a');
    a.href = href;
    a.id = id;
    a.className = className;
    a.setAttribute('aria-label', ariaLabel);
    a.innerHTML = icon(iconId, 20, 20, title);
    parent.appendChild(a);
}

function appendActionButton(
    parent: HTMLElement,
    id: string,
    className: string,
    iconId: string,
    ariaLabel: string,
    title: string | boolean,
    display: string,
    onClick: () => void | Promise<void>,
): HTMLButtonElement {
    const button = document.createElement('button');
    button.id = id;
    button.type = 'button';
    button.className = className;
    button.style.display = display;
    button.setAttribute('aria-label', ariaLabel);
    button.innerHTML = icon(iconId, 20, 20, title);
    button.addEventListener('click', () => { void onClick(); });
    parent.appendChild(button);
    return button;
}

function renderWorldManagementActions(parent: HTMLElement, card: ManagedWorldCard): void {
    const locationId = card.item_id;
    const privacyUrl = resolveUrl('options/db_queries/update/update_privacy_inc.php');
    const deleteUrl = resolveUrl('options/db_queries/delete/soft_delete_inc.php');

    // World timeline (world_timeline.php) — the world's history as era bands + per-continent
    // lanes. Built from the world's location_id, so no extra endpoint field is needed.
    appendIconButton(parent, `${resolveUrl('world_timeline.php')}?world_location_id=${encodeURIComponent(locationId)}`, `btn_timeline_location_${locationId}`, 'btn btn-sm btn-secondary', 'timeline', 'Timeline', 'Timeline - this world’s history');
    appendIconButton(parent, card.media_url, `btn_media_manager_location_${locationId}`, 'btn btn-sm btn-info', 'modify_picture_gallery', 'Media manager', 'Media Manager - picture gallery + upload');

    if (card.show_private) {
        const publicButton = appendActionButton(
            parent,
            `location_public_${locationId}`,
            'btn btn-primary btn-sm',
            'public',
            'Make private',
            'Public',
            card.is_private ? 'none' : 'block',
            async () => {
                const result = await queryData(privacyUrl, 'div_sidelog_warning', {
                    item_type: 'location',
                    location_id: locationId,
                    location_private: '1',
                    csrf_token: card.csrf_token,
                });
                if (result === null) return;
                publicButton.style.display = 'none';
                privateButton.style.display = 'block';
            },
        );

        const privateButton = appendActionButton(
            parent,
            `location_private_${locationId}`,
            'btn btn-danger btn-sm',
            'private',
            'Make public',
            'Private',
            card.is_private ? 'block' : 'none',
            async () => {
                const result = await queryData(privacyUrl, 'div_sidelog_warning', {
                    item_type: 'location',
                    location_id: locationId,
                    location_private: '0',
                    csrf_token: card.csrf_token,
                });
                if (result === null) return;
                privateButton.style.display = 'none';
                publicButton.style.display = 'block';
            },
        );
    }

    const deleteButton = appendActionButton(
        parent,
        `location_delete_${locationId}`,
        'btn btn-warning btn-sm',
        'delete',
        'Delete world',
        'Delete',
        'block',
        () => {
            deleteButton.style.display = 'none';
            confirmButton.style.display = 'block';
        },
    );

    const confirmButton = appendActionButton(
        parent,
        `location_confirm_${locationId}`,
        'btn btn-danger btn-sm',
        'confirm',
        'Confirm delete',
        'Confirm',
        'none',
        async () => {
            if (typeof openSideLog === 'function') openSideLog();
            await queryData(deleteUrl, 'div_sidelog_warning', {
                item_type: 'location',
                location_id: locationId,
                display_success_sidelog: '1',
                csrf_token: card.csrf_token,
            }, true, true, true, `js-my-world-card-${locationId}`);
        },
    );
}

// The shared card: picture + title linking into the World Book. Returns the column and the card
// shell the caller appends its button rows to.
function renderWorldCardShell(card: { item_id: string; name: string; url: string; picture_url: string }): { col: HTMLElement; shell: HTMLElement; body: HTMLElement } {
    const col = document.createElement('div');
    col.className = 'col-12 col-md-6 col-lg-4 col-xl-3 p-2 js-my-world-card';
    col.id = `js-my-world-card-${card.item_id}`;
    col.dataset.worldName = card.name || '';

    const shell = document.createElement('div');
    shell.className = 'card text-left w-100 h-100 shadow-sm border border-secondary border-opacity-25 d-flex flex-column';
    shell.setAttribute('data-bs-theme', 'dark');
    col.appendChild(shell);

    const link = document.createElement('a');
    link.href = card.url;
    link.className = 'text-decoration-none flex-shrink-0';
    shell.appendChild(link);

    const mediaFrame = document.createElement('div');
    mediaFrame.className = 'overflow-hidden w-100';
    mediaFrame.style.aspectRatio = '16 / 9';
    link.appendChild(mediaFrame);

    const img = document.createElement('img');
    img.src = card.picture_url || '';
    img.className = 'w-100 h-100';
    img.alt = card.name || 'World';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.style.objectFit = 'cover';
    img.style.objectPosition = 'center';
    mediaFrame.appendChild(img);

    const body = document.createElement('div');
    body.className = 'card-body d-flex flex-column flex-grow-1 py-2 px-3';
    shell.appendChild(body);

    const title = document.createElement('h6');
    title.className = 'card-title small fw-semibold lh-sm mb-0 text-white text-break';
    title.setAttribute('style', CLAMP_2);
    title.textContent = card.name || 'Untitled';
    body.appendChild(title);

    return { col, shell, body };
}

function renderMyWorldCard(card: ManagedWorldCard): HTMLElement {
    const { col, shell } = renderWorldCardShell(card);

    const primary = document.createElement('div');
    primary.className = 'border-top border-secondary border-opacity-25 flex-shrink-0 px-3 py-2';
    const primaryGroup = document.createElement('div');
    primaryGroup.className = 'd-flex flex-wrap align-items-center gap-1';
    // 7th arg is iconMap()'s `title`: omit it and it defaults to `false`, which makes iconMap()
    // return a BARE <img> with no tooltip wrapper — the aria-label alone never surfaces on hover.
    // Pass the label explicitly so this row popups like the management row below does.
    appendIconButton(primaryGroup, card.url, `btn_view_location_${card.item_id}`, 'btn btn-sm btn-success', 'world_locations', 'View world', 'View World - this world’s locations');
    appendIconButton(primaryGroup, card.edit_url, `btn_edit_location_${card.item_id}`, 'btn btn-sm btn-danger', 'edit_location', 'Edit world', 'Edit World - name, description, privacy');
    appendIconButton(primaryGroup, card.add_location_url, `btn_add_location_${card.item_id}`, 'btn btn-sm btn-primary', 'add_location', 'Add location', 'Add Location - create a location in this world');
    primary.appendChild(primaryGroup);
    shell.appendChild(primary);

    const secondary = document.createElement('div');
    secondary.className = 'border-top border-secondary border-opacity-25 flex-shrink-0 px-3 py-2';
    const secondaryGroup = document.createElement('div');
    secondaryGroup.className = 'd-flex flex-wrap align-items-center gap-1';
    renderWorldManagementActions(secondaryGroup, card);
    secondary.appendChild(secondaryGroup);
    shell.appendChild(secondary);

    const response = document.createElement('div');
    response.id = `div_refresh_world_${card.item_id}`;
    shell.appendChild(response);

    return col;
}

function renderWorldBrowseCard(card: WorldBrowseCard): HTMLElement {
    const { col, shell, body } = renderWorldCardShell(card);

    const meta = document.createElement('div');
    meta.className = 'small text-white-50 mt-1';
    meta.textContent = card.place_count === 1 ? '1 place' : `${card.place_count.toLocaleString()} places`;
    body.appendChild(meta);

    const actions = document.createElement('div');
    actions.className = 'border-top border-secondary border-opacity-25 flex-shrink-0 px-3 py-2';
    const group = document.createElement('div');
    group.className = 'd-flex flex-wrap align-items-center gap-1';
    appendIconButton(group, card.url, `btn_view_location_${card.item_id}`, 'btn btn-sm btn-success', 'world_locations', 'Open World Book', 'Open World Book - this world’s places and lore');
    appendIconButton(group, card.timeline_url, `btn_timeline_location_${card.item_id}`, 'btn btn-sm btn-secondary', 'timeline', 'Timeline', 'Timeline - this world’s history');
    actions.appendChild(group);
    shell.appendChild(actions);

    return col;
}

interface BuiltCard { el: HTMLElement; text: string; }

interface WorldCardsResponse<C> { cards: C[]; total: number; }

export function mountCatalogMyWorlds(root: HTMLElement, config: CatalogListConfig): void {
    mountWorldCards(root, config, 'catalog-my-worlds', renderMyWorldCard, myWorldCards);
}

export function mountCatalogWorlds(root: HTMLElement, config: CatalogListConfig): void {
    mountWorldCards(root, config, 'catalog-worlds', renderWorldBrowseCard, worldBrowseCards);
}

function mountWorldCards<C extends { name: string; text: string }>(
    root: HTMLElement,
    config: CatalogListConfig,
    tag: string,
    render: (card: C) => HTMLElement,
    adopt: (data: WorldListResponse) => C[],
): void {
    const container = results(root);
    if (!container) {
        console.error(`[${tag}] mount missing [data-ft-catalog-results]`, root);
        return;
    }

    const searchInput = config.searchInputId
        ? (document.getElementById(config.searchInputId) as HTMLInputElement | null)
        : null;
    const emptyText = root.getAttribute('data-ft-empty-text') || 'No worlds found.';

    void (async () => {
        const loadingTimer = setTimeout(() => showLoader(root, config), 250);
        try {
            const res = await fetch(resolveUrl(config.endpoint), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: '{}',
            });
            const raw = await res.text();
            let data: WorldListResponse;
            try {
                data = JSON.parse(raw) as WorldListResponse;
            } catch {
                throw new Error(`Non-JSON response (HTTP ${res.status}): ${raw.slice(0, 300)}`);
            }
            const errData = data as unknown as { error?: string; detail?: string };
            if (!res.ok || errData.error) {
                throw new Error(errData.detail || errData.error || `HTTP ${res.status}`);
            }
            if (data.schema !== 'world-list-v1' || !Array.isArray(data.worlds) || data.mode !== (tag === 'catalog-my-worlds' ? 'my' : 'browse')) throw new Error('world-list-schema');
            clearTimeout(loadingTimer);
            hideLoader(root);
            setStatus(root, '');
            build(root, container, { cards: adopt(data), total: data.total }, searchInput, emptyText, render);
        } catch (e) {
            clearTimeout(loadingTimer);
            hideLoader(root);
            console.error(`[${tag}] fetch/render failed`, e);
            setStatus(root, '');
            container.innerHTML = '';
            const msg = document.createElement('div');
            msg.className = 'col-12 text-center text-muted py-4';
            msg.textContent = 'Could not load worlds. Please try again.';
            container.appendChild(msg);
        }
    })();
}

function build<C extends { name: string; text: string }>(
    root: HTMLElement,
    container: HTMLElement,
    data: WorldCardsResponse<C>,
    searchInput: HTMLInputElement | null,
    emptyText: string,
    render: (card: C) => HTMLElement,
): void {
    container.innerHTML = '';

    const built: BuiltCard[] = [];
    for (const card of data.cards || []) {
        const el = render(card);
        container.appendChild(el);
        built.push({ el, text: card.text || (card.name || '').toLowerCase() });
    }

    initTooltips(container);

    const emptyEl = document.createElement('div');
    emptyEl.className = 'col-12 text-center text-muted py-5';
    emptyEl.textContent = emptyText;
    emptyEl.style.display = built.length ? 'none' : '';
    container.appendChild(emptyEl);
    const emptySpec = built.length === 0 ? emptyStateFromAttributes(root, emptyText) : null;
    if (emptySpec) {
        emptyEl.className = 'col-12';
        emptyEl.replaceChildren(buildEmptyState(emptySpec));
    }

    const applyFilter = (): void => {
        const keyword = (searchInput?.value || '').trim().toLowerCase();
        let visible = 0;
        for (const c of built) {
            const show = !keyword || c.text.includes(keyword);
            c.el.style.display = show ? '' : 'none';
            if (show) visible++;
        }
        if (!emptySpec) emptyEl.textContent = keyword ? 'No worlds match your search.' : emptyText;
        emptyEl.style.display = visible === 0 ? '' : 'none';
    };

    if (searchInput) {
        const box = searchInput.closest('.ft-inline-search-root');
        const glyph = box?.querySelector('[data-ft-inline-search-role="glyph"]') ?? null;
        const clear = box?.querySelector('[data-ft-inline-search-role="clear"]') ?? null;
        const syncTrailing = (): void => {
            if (!glyph || !clear) return;
            const hasText = (searchInput.value || '').trim().length > 0;
            glyph.classList.toggle('d-none', hasText);
            clear.classList.toggle('d-none', !hasText);
        };
        searchInput.addEventListener('input', () => { syncTrailing(); applyFilter(); });
        if (clear) {
            clear.addEventListener('click', (e) => {
                e.preventDefault();
                searchInput.value = '';
                searchInput.focus();
                syncTrailing();
                applyFilter();
            });
        }
        syncTrailing();
    }

    applyFilter();
}
